import * as React from 'react';

import { Button, CircularProgress, Typography } from '@mui/material';
import { Download, Eye, FileText } from 'lucide-react';

import {
  downloadArtifact,
  isArtifactNotFound,
} from '@modules/_shared/components/chat/artifactApi';
import {
  type ArtifactSummary,
  formatFileSize,
  isThumbnailImage,
  previewKindOf,
} from '@modules/_shared/components/chat/artifactFiles';
import { ArtifactThumbnail } from '@modules/_shared/components/chat/ArtifactThumbnail';
import { useArtifactPreview } from '@modules/_shared/components/chat/useArtifactPreview';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

type FileState =
  | 'idle'
  | 'busy'
  | 'gone'
  | 'download-failed'
  | 'preview-failed';

// Download and Preview of one of the user's files in a chat card. Both ask the
// chat API for a fresh signed URL (never stored in the message); the preview
// is the Files view's (useArtifactPreview).
export const useChatFile = (artifact: ArtifactSummary) => {
  const [state, setState] = React.useState<FileState>('idle');
  const failedWith = (error: unknown, failure: FileState) =>
    setState(isArtifactNotFound(error) ? 'gone' : failure);

  const download = async () => {
    setState('busy');
    try {
      await downloadArtifact(artifact.id);
      setState('idle');
    } catch (error) {
      failedWith(error, 'download-failed');
    }
  };

  const previews = useArtifactPreview({
    onGone: () => setState('gone'),
    onFailure: (_, error) => failedWith(error, 'preview-failed'),
    onDownload: () => void download(),
  });

  const preview = async () => {
    setState('busy');
    try {
      await previews.preview(artifact);
      // An HTML preview may already have reported the file gone.
      setState(current => (current === 'busy' ? 'idle' : current));
    } catch (error) {
      failedWith(error, 'preview-failed');
    }
  };

  return { state, download, preview, dialogs: previews.dialogs };
};

type ChatFile = ReturnType<typeof useChatFile>;

export const useFileStateText = (state: FileState): string | null => {
  const { t } = useLocale();
  switch (state) {
    case 'gone':
      return t('This file was deleted, or it was saved in another agency.');
    case 'download-failed':
      return t('Failed to download the file');
    case 'preview-failed':
      return t('Failed to load the preview');
    case 'idle':
    case 'busy':
      return null;
  }
};

// One of the user's files: name, size, Preview (when the Files view previews
// that type) and Download.
const FileRow = ({
  artifact,
  file,
}: {
  artifact: ArtifactSummary;
  file: ChatFile;
}) => {
  const { t, intlLocale } = useLocale();
  const { colors } = useTheme();
  const stateText = useFileStateText(file.state);
  const busy = file.state === 'busy';
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        backgroundColor: colors.white,
      }}
    >
      <FileText size={18} color={colors.grey600} style={{ flexShrink: 0 }} />
      <div css={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          title={artifact.name}
          sx={{ fontWeight: 500 }}
        >
          {artifact.name}
        </Typography>
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: stateText == null ? colors.grey600 : colors.errorText,
          }}
        >
          {stateText ?? formatFileSize(artifact.sizeBytes, intlLocale)}
        </Typography>
      </div>
      {file.state !== 'gone' && (
        <>
          {previewKindOf(artifact) != null && (
            <Button
              size="small"
              variant="text"
              disabled={busy}
              onClick={() => void file.preview()}
              startIcon={<Eye size={14} />}
            >
              {t('Preview')}
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => void file.download()}
            startIcon={
              busy ? <CircularProgress size={12} /> : <Download size={14} />
            }
          >
            {t('Download')}
          </Button>
        </>
      )}
      {file.dialogs}
    </div>
  );
};

export const ChatFileRow = ({ artifact }: { artifact: ArtifactSummary }) => {
  const file = useChatFile(artifact);
  return <FileRow artifact={artifact} file={file} />;
};

// Card under a finished save_artifact call: the file Aura saved, with a
// thumbnail for an image. Running and failed calls stay steps
// (messageSegments.ts).
export const SavedFileCard = ({ artifact }: { artifact: ArtifactSummary }) => {
  const file = useChatFile(artifact);
  if (!isThumbnailImage(artifact)) {
    return <FileRow artifact={artifact} file={file} />;
  }
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {file.state !== 'gone' && (
        <ArtifactThumbnail
          artifact={artifact}
          onOpen={() => void file.preview()}
        />
      )}
      <FileRow artifact={artifact} file={file} />
    </div>
  );
};
